import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Intent } from '@blueprintjs/core';
import { useParams } from 'react-router-dom';

import { UserAccess } from 'components/user-access';
import { PageRestricted } from 'pages/errors/page-restricted';
import { Breadcrumbs } from 'components/navigation';
import { DataDisplayRecord } from 'components/data-display';
import { GridLayout } from 'components/grid';
import { DateToLocal } from 'components/dates';
import { Numeric } from 'components/formatting';
import { ButtonSave } from 'components/buttons';
import { ShowHide } from 'components/layout';
import { NotificationInline, NotificationToaster } from 'components/notifications';
import { BulkImportService, BlobStorageService } from 'services';

import { importDetailActions, importSources } from './bulkImportTypes';

const forbiddenCode = 403;

const pollIntervalMs = 10000;

const inProgressStatuses = ["Queued", "Processing"];

const noValue = "-";

const generalErrorMessage = "An error occurred while loading the import. Please try again.";
const downloadErrorMessage = "Could not download the file. Please try again.";

export function BulkImportDetail({ breadcrumbs }) {

    const { sourceKey, id } = useParams();

    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloadingErrors, setDownloadingErrors] = useState(false);
    const [downloadingOriginal, setDownloadingOriginal] = useState(false);

    const [isRestricted, setIsRestricted] = useState(false);

    const isKnownSource = importSources.some((source) => source.sourceKey === sourceKey);

    useEffect(() => {
        if (!isKnownSource) {
            setIsRestricted(true);
            return;
        }

        setIsRestricted(false);
        setDetail(null);
        setLoading(true);

        BulkImportService.get(sourceKey, id).then(
            (response) => {
                setDetail(response);
                setLoading(false);
            },
            (error) => {
                if (error?.response?.status === forbiddenCode) {
                    setIsRestricted(true);
                } else {
                    NotificationToaster.show(Intent.DANGER, generalErrorMessage);
                }
                setLoading(false);
            }
        );
    }, [sourceKey, id, isKnownSource]);

    const isPolling = !isRestricted && detail != null && inProgressStatuses.includes(detail.status);

    const breadcrumbItems = detail != null
        ? [...breadcrumbs, { title: detail.fileName }]
        : breadcrumbs;

    useEffect(() => {
        if (!isPolling) {
            return undefined;
        }

        const timer = setInterval(() => {
            BulkImportService.get(sourceKey, id).then(
                (response) => {
                    setDetail(response);
                },
                () => {
                    clearInterval(timer);
                }
            );
        }, pollIntervalMs);

        return () => {
            clearInterval(timer);
        };
    }, [isPolling, sourceKey, id]);

    function downloadFile(file, setDownloading) {
        setDownloading(true);
        BlobStorageService.downloadFile(
            "https://" + file.storageName + ".blob.core.windows.net",
            file.containerName,
            file.fileName,
            file.token,
            file.downloadName
        ).then(
            () => {
            },
            () => {
                NotificationToaster.show(Intent.DANGER, downloadErrorMessage);
            }
        ).finally(
            () => setDownloading(false)
        );
    }

    function renderStatusBanner() {
        if (detail == null) {
            return null;
        }

        switch (detail.status) {
            case "Queued":
                return (<NotificationInline intent={Intent.PRIMARY} show allowClose={false} text="Queued - waiting to be picked up for processing." />);
            case "Processing":
                return (<NotificationInline intent={Intent.PRIMARY} show allowClose={false} text={(<>Processing - <Numeric>{(detail.successRows ?? 0) + (detail.errorRows ?? 0)}</Numeric> rows processed so far.</>)} />);
            case "Completed":
                return (<NotificationInline intent={Intent.SUCCESS} show allowClose={false} text="All rows imported successfully." />);
            case "Completed With Errors":
                return (<NotificationInline intent={Intent.WARNING} show allowClose={false} text={(<><Numeric>{detail.errorRows}</Numeric> of <Numeric>{detail.totalRows}</Numeric> rows failed. Download the error file, fix the highlighted cells, and re-upload.</>)} />);
            case "Failed":
                return (<NotificationInline intent={Intent.DANGER} show allowClose={false} text="The import could not be processed." />);
            default:
                return null;
        }
    }

    return (
        <UserAccess
            perform={importDetailActions}
            yes={() => (
                <ShowHide
                    evaluator={isRestricted}
                    show={(<PageRestricted />)}
                    hide={(
                        <div>
                            {/* The breadcrumbs sit outside the detail block so a load failure still
                                leaves the user a way back, with only the import's own content missing. */}
                            <ShowHide
                                evaluator={loading}
                                hide={<Breadcrumbs items={breadcrumbItems} />}
                            />

                            <ShowHide
                                evaluator={loading || detail != null}
                                show={
                                    <>
                                        <h1 className={classNames({ "bp6-skeleton": loading })}>{detail?.fileName}</h1>

                                        {renderStatusBanner()}

                                        <GridLayout gap={40} numberOfColumns={4} className="spacer-top">
                                            <DataDisplayRecord loading={loading} header="Import Type" data={detail?.bulkImportTypeName ?? noValue} />
                                            <DataDisplayRecord loading={loading} header="Total Rows" data={detail?.totalRows != null ? (<Numeric>{detail.totalRows}</Numeric>) : noValue} />
                                            <DataDisplayRecord loading={loading} header="Successful" data={detail?.successRows != null ? (<Numeric>{detail.successRows}</Numeric>) : noValue} />
                                            <DataDisplayRecord loading={loading} header="Errors" data={detail?.errorRows != null ? (<Numeric>{detail.errorRows}</Numeric>) : noValue} />
                                        </GridLayout>

                                        <GridLayout gap={40} numberOfColumns={4} className="spacer-top">
                                            <DataDisplayRecord loading={loading} header="Created" data={detail?.createdDate != null ? (<DateToLocal>{detail.createdDate}</DateToLocal>) : noValue} />
                                            <DataDisplayRecord loading={loading} header="Started" data={detail?.startedDate != null ? (<DateToLocal>{detail.startedDate}</DateToLocal>) : noValue} />
                                            <DataDisplayRecord loading={loading} header="Completed" data={detail?.completedDate != null ? (<DateToLocal>{detail.completedDate}</DateToLocal>) : noValue} />
                                        </GridLayout>

                                        <ShowHide
                                            evaluator={detail?.status === "Failed" && detail?.failureReason != null}
                                            show={(
                                                <div className="spacer-top">
                                                    <DataDisplayRecord header="Failure reason" data={detail?.failureReason} />
                                                </div>
                                            )}
                                        />

                                        <ShowHide
                                            evaluator={detail != null}
                                            show={(
                                                <div className="button-row button-row-stacked-mobile spacer-top">
                                                    <ShowHide
                                                        evaluator={detail?.errorFile != null}
                                                        show={(
                                                            <ButtonSave
                                                                intent="primary"
                                                                text="Download error file"
                                                                id="bulk-import-download-error"
                                                                onClick={() => downloadFile(detail.errorFile, setDownloadingErrors)}
                                                                disabled={downloadingErrors}
                                                                icon={null}
                                                            />
                                                        )}
                                                    />
                                                    <ButtonSave
                                                        intent={detail?.errorFile != null ? "secondary" : "primary"}
                                                        text="Download original file"
                                                        id="bulk-import-download-original"
                                                        onClick={() => downloadFile(detail.originalFile, setDownloadingOriginal)}
                                                        disabled={downloadingOriginal}
                                                        icon={null}
                                                    />
                                                </div>
                                            )}
                                        />
                                    </>
                                }
                            />
                        </div>
                    )}
                />
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

BulkImportDetail.propTypes = {
    breadcrumbs: PropTypes.array
};
